import {
  Button,
  Checkbox,
  cn,
  Modal,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { PlusIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass } from "@/lib/utils/cell-input";
import { fetchAllCompanies } from "./components";
import type { WorkContractRow } from "./work-contracts-editable-panel";

type ContractIntervention = {
  id: number;
  lotLabel: string;
  performanceName: string;
  buildingShortName: string;
  zoneName: string;
};

type TakeoverMode = "all" | "grid";

const contractLabel = (contract: WorkContractRow): string =>
  isNotNil(contract.shortName)
    ? `${contract.performer.name} (${contract.shortName})`
    : contract.performer.name;

export type ContractInterruptDialogProps = {
  yardId: number;
  /** Contract being interrupted; the dialog is open while it is set. */
  contract: WorkContractRow | null;
  /** Every contract of the yard (active and interrupted). */
  contracts: WorkContractRow[];
  subContractCount: number;
  yardMoaCompany: { id: number; name: string } | null;
  onClose: () => void;
  onInterrupted: () => void;
};

/**
 * Interruption of a work contract with the takeover of its
 * interventions: the user picks — or creates — the contract(s) taking
 * over, then hands them every intervention or picks them in a light grid.
 * Interventions left out stay flagged "à reprendre" in the matrix.
 */
export function ContractInterruptDialog({
  yardId,
  contract,
  contracts,
  subContractCount,
  yardMoaCompany,
  onClose,
  onInterrupted,
}: ContractInterruptDialogProps) {
  const [interventions, setInterventions] = useState<ContractIntervention[]>(
    [],
  );
  const [loadingInterventions, setLoadingInterventions] = useState(false);
  // Contracts created from this dialog, not yet in the parent's refetched list.
  const [createdContracts, setCreatedContracts] = useState<WorkContractRow[]>(
    [],
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mode, setMode] = useState<TakeoverMode>("all");
  // Grid mode: interventionId -> the one contract taking it over (an
  // intervention has a single active contract per rang).
  const [grid, setGrid] = useState<Map<number, number>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [newPerformerId, setNewPerformerId] = useState<number | null>(null);
  const [newShortName, setNewShortName] = useState("");
  const [newParentId, setNewParentId] = useState<number | null>(null);

  const contractId = contract?.id ?? null;
  const isRang2 = contract?.type === "travaux_n_2";

  useEffect(() => {
    setCreatedContracts([]);
    setSelectedIds([]);
    setMode("all");
    setGrid(new Map());
    setIsCreating(false);
    setInterventions([]);
    if (isNil(contractId)) return;

    let cancelled = false;
    setLoadingInterventions(true);
    apiClient
      .GET("/api/admin/contracts/{id}/interventions", {
        params: { path: { id: contractId } },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setLoadingInterventions(false);
        if (isNil(data)) {
          toaster.error({
            title: "Erreur",
            description: "Échec du chargement des interventions du contrat",
          });
          return;
        }
        setInterventions(data.data.interventions);
      });
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  useEffect(() => {
    if (!isCreating || companyOptions.length > 0) return;
    fetchAllCompanies().then(({ companies }) => {
      if (isNil(companies)) return;
      setCompanyOptions(
        companies.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      );
    });
  }, [isCreating, companyOptions.length]);

  if (isNil(contract)) return null;

  const candidates = [
    ...contracts.filter(
      (c) => c.type === contract.type && c.isActive && c.id !== contract.id,
    ),
    ...createdContracts,
  ];
  const selectedContracts = candidates.filter((c) =>
    selectedIds.includes(c.id),
  );
  const activeRang1Options: SelectOption[] = contracts
    .filter((c) => c.type === "travaux_n_1" && c.isActive)
    .map((c) => ({ label: contractLabel(c), value: String(c.id) }));

  const toggleSelected = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id),
    );
    if (!checked) {
      setGrid(
        (prev) =>
          new Map([...prev].filter(([, contractId]) => contractId !== id)),
      );
    }
  };

  const isGridChecked = (interventionId: number, id: number) =>
    grid.get(interventionId) === id;

  // Ticking a contract on a row replaces the one ticked before: a row keeps
  // at most one takeover contract.
  const toggleGridCell = (
    interventionId: number,
    id: number,
    checked: boolean,
  ) => {
    setGrid((prev) => {
      const next = new Map(prev);
      if (checked) next.set(interventionId, id);
      else if (next.get(interventionId) === id) next.delete(interventionId);
      return next;
    });
  };

  const isColumnChecked = (id: number) =>
    interventions.length > 0 &&
    interventions.every((i) => isGridChecked(i.id, id));

  const toggleColumn = (id: number, checked: boolean) => {
    setGrid((prev) => {
      const next = new Map(prev);
      for (const intervention of interventions) {
        if (checked) next.set(intervention.id, id);
        else if (next.get(intervention.id) === id) {
          next.delete(intervention.id);
        }
      }
      return next;
    });
  };

  // Several takeover contracts can only be split through the grid.
  const effectiveMode: TakeoverMode = selectedIds.length > 1 ? "grid" : mode;
  const takeovers =
    selectedIds.length === 0
      ? []
      : effectiveMode === "all"
        ? interventions.map((i) => ({
            interventionId: i.id,
            contractId: selectedIds[0],
          }))
        : interventions.flatMap((i) => {
            const takeoverId = grid.get(i.id);
            return isNotNil(takeoverId)
              ? [{ interventionId: i.id, contractId: takeoverId }]
              : [];
          });
  const leftCount = interventions.length - takeovers.length;

  const openCreate = () => {
    setNewPerformerId(null);
    setNewShortName("");
    const parentStillActive = contracts.some(
      (c) => c.id === contract.parentContractId && c.isActive,
    );
    setNewParentId(parentStillActive ? contract.parentContractId : null);
    setIsCreating(true);
  };

  const createContract = async () => {
    if (isNil(newPerformerId)) {
      toaster.error({
        title: "Erreur",
        description: "Merci de sélectionner une société contractante.",
      });
      return;
    }
    if (isRang2 && isNil(newParentId)) {
      toaster.error({
        title: "Erreur",
        description: "Merci de sélectionner un donneur d'ordre (rang 1).",
      });
      return;
    }
    const shortName = newShortName.trim() ? newShortName.trim() : null;
    const { data, error } = await apiClient.POST("/api/admin/contracts", {
      body: {
        yardId,
        type: contract.type,
        performerId: newPerformerId,
        shortName,
        groupId: null,
        ...(isRang2
          ? { parentContractId: newParentId }
          : { customerId: yardMoaCompany?.id ?? null }),
      },
    });
    if (isNil(data)) {
      toaster.error({
        title: "Erreur",
        description: error?.error || "Échec de la création du contrat",
      });
      return;
    }
    const created = data.data.contract;
    const performerName =
      companyOptions.find((o) => o.value === String(newPerformerId))?.label ??
      "";
    setCreatedContracts((prev) => [
      ...prev,
      {
        id: created.id,
        type: contract.type,
        shortName,
        isActive: true,
        groupId: null,
        customerId: null,
        parentContractId: isRang2 ? newParentId : null,
        hasInterventions: false,
        performer: { id: newPerformerId, name: performerName },
        customer: null,
        group: null,
        referent: null,
        referentHistory: [],
        teams: [],
      },
    ]);
    setSelectedIds((prev) => [...prev, created.id]);
    setIsCreating(false);
  };

  const submit = async () => {
    setIsSubmitting(true);
    const { error } = await apiClient.PATCH(
      "/api/admin/contracts/{id}/interrupt",
      {
        params: { path: { id: contract.id } },
        body: { takeovers },
      },
    );
    setIsSubmitting(false);
    if (isNotNil(error)) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de l'interruption du contrat",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Contrat interrompu" });
    onInterrupted();
  };

  // Created contracts already exist even if the dialog is dismissed: the
  // parent must refetch so they show up in the contracts table.
  const close = () => {
    if (createdContracts.length > 0) onInterrupted();
    else onClose();
  };

  return (
    <Modal.Root
      open
      onOpenChange={(details) => {
        if (!details.open && !isSubmitting) close();
      }}
    >
      <Modal.Portal>
        <Modal.Backdrop />
        {/* From the top: centred, it moved at each tick that made it taller. */}
        <Modal.Positioner className="!items-start">
          <Modal.Content size="2xl">
            <Modal.Header>
              <Modal.Title>Interrompre le contrat</Modal.Title>
            </Modal.Header>
            <Modal.Body className="space-y-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Le contrat{" "}
                <span className="font-medium">{contractLabel(contract)}</span>{" "}
                ne pourra plus être affecté à de nouvelles interventions.
                Choisissez le ou les contrats qui reprennent ses interventions.
              </p>
              {subContractCount > 0 && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Contrats sous-traités (rang 2) rattachés à ce contrat :{" "}
                  {subContractCount}. Ils le resteront jusqu'à ce que vous
                  choisissiez, dans leur colonne « Donneur d'ordre », le contrat
                  rang 1 qui reprend la mission.
                </p>
              )}

              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    Contrats repreneurs
                  </h4>
                  {!isCreating && (
                    <Button plain onClick={openCreate}>
                      <PlusIcon data-slot="icon" className="size-4" />
                      Nouveau contrat
                    </Button>
                  )}
                </div>
                {candidates.length === 0 && !isCreating && (
                  <p className="text-sm text-zinc-500">
                    Aucun autre contrat actif de ce rang. Créez le contrat qui
                    reprend les interventions.
                  </p>
                )}
                <div className="max-h-40 space-y-2 overflow-y-auto">
                  {candidates.map((candidate) => (
                    <div key={candidate.id}>
                      <Checkbox
                        checked={selectedIds.includes(candidate.id)}
                        onCheckedChange={(details) =>
                          toggleSelected(candidate.id, details.checked === true)
                        }
                      >
                        {contractLabel(candidate)}
                      </Checkbox>
                    </div>
                  ))}
                </div>
                {isCreating && (
                  <div className="flex items-center gap-2 rounded-lg border border-zinc-200 p-2 dark:border-zinc-800">
                    <div className="min-w-0 flex-[2]">
                      <Select
                        flat
                        options={companyOptions}
                        value={
                          isNotNil(newPerformerId)
                            ? [String(newPerformerId)]
                            : []
                        }
                        onValueChange={(details) => {
                          const val = details.value[0];
                          setNewPerformerId(val ? Number(val) : null);
                        }}
                        placeholder="Société"
                      />
                    </div>
                    <input
                      type="text"
                      value={newShortName}
                      onChange={(e) => setNewShortName(e.target.value)}
                      maxLength={50}
                      className={cn(cellInputClass(false), "w-24 flex-none")}
                      placeholder="Abrév."
                    />
                    {isRang2 && (
                      <div className="min-w-0 flex-[2]">
                        <Select
                          flat
                          options={activeRang1Options}
                          value={
                            isNotNil(newParentId) ? [String(newParentId)] : []
                          }
                          onValueChange={(details) => {
                            const val = details.value[0];
                            setNewParentId(val ? Number(val) : null);
                          }}
                          placeholder="Donneur d'ordre"
                        />
                      </div>
                    )}
                    <Button plain onClick={() => setIsCreating(false)}>
                      Annuler
                    </Button>
                    <Button onClick={createContract}>Créer</Button>
                  </div>
                )}
              </section>

              {selectedContracts.length > 0 && (
                <section className="space-y-2">
                  <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    Interventions reprises
                  </h4>
                  {loadingInterventions ? (
                    <p className="text-sm text-zinc-500">Chargement...</p>
                  ) : interventions.length === 0 ? (
                    <p className="text-sm text-zinc-500">
                      Ce contrat ne couvre aucune intervention.
                    </p>
                  ) : (
                    <>
                      {selectedIds.length > 1 ? (
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                          Plusieurs contrats reprennent les interventions :
                          choisissez dans la grille le contrat qui reprend
                          chacune d'elles.
                        </p>
                      ) : (
                        <div className="flex gap-4 text-sm">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={mode === "all"}
                              onChange={() => setMode("all")}
                              className="text-blue-600"
                            />
                            Toutes les interventions ({interventions.length})
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={mode === "grid"}
                              onChange={() => setMode("grid")}
                              className="text-blue-600"
                            />
                            Choisir dans la grille
                          </label>
                        </div>
                      )}
                      {effectiveMode === "grid" && (
                        <div className="overflow-clip rounded-lg border border-zinc-200 dark:border-zinc-800">
                          <table className="w-full table-fixed border-collapse text-sm">
                            <colgroup>
                              <col style={{ width: "calc(8ch + 16px)" }} />
                              <col />
                              <col style={{ width: "calc(20ch + 16px)" }} />
                              {selectedContracts.map((c) => (
                                <col
                                  key={c.id}
                                  style={{ width: "calc(12ch + 16px)" }}
                                />
                              ))}
                            </colgroup>
                            <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
                              <tr className="text-left">
                                <th className="truncate p-2 font-medium">
                                  Lot
                                </th>
                                <th className="truncate p-2 font-medium">
                                  Presta
                                </th>
                                <th className="truncate p-2 font-medium">
                                  Zone
                                </th>
                                {selectedContracts.map((c) => (
                                  <th
                                    key={c.id}
                                    className="p-2 font-medium"
                                    title={contractLabel(c)}
                                  >
                                    <Checkbox
                                      checked={isColumnChecked(c.id)}
                                      onCheckedChange={(details) =>
                                        toggleColumn(
                                          c.id,
                                          details.checked === true,
                                        )
                                      }
                                      aria-label={`Tout attribuer à ${contractLabel(c)}`}
                                    >
                                      <span className="block truncate">
                                        {c.shortName ?? c.performer.name}
                                      </span>
                                    </Checkbox>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {interventions.map((intervention) => {
                                const zone = `${intervention.buildingShortName} · ${intervention.zoneName}`;
                                return (
                                  <tr
                                    key={intervention.id}
                                    className="h-9 border-t border-zinc-100 dark:border-zinc-800"
                                  >
                                    <td
                                      className="truncate p-2"
                                      title={intervention.lotLabel}
                                    >
                                      {intervention.lotLabel}
                                    </td>
                                    <td
                                      className="truncate p-2"
                                      title={intervention.performanceName}
                                    >
                                      {intervention.performanceName}
                                    </td>
                                    <td className="truncate p-2" title={zone}>
                                      {zone}
                                    </td>
                                    {selectedContracts.map((c) => (
                                      <td key={c.id} className="p-2">
                                        <Checkbox
                                          checked={isGridChecked(
                                            intervention.id,
                                            c.id,
                                          )}
                                          onCheckedChange={(details) =>
                                            toggleGridCell(
                                              intervention.id,
                                              c.id,
                                              details.checked === true,
                                            )
                                          }
                                          aria-label={`${intervention.performanceName} ${zone} : ${contractLabel(c)}`}
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {interventions.length > 0 && leftCount > 0 && (
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {leftCount === interventions.length
                    ? "Aucune intervention n'est reprise : elles resteront à reprendre, case par case, dans la matrice."
                    : `${leftCount} intervention(s) resteront à reprendre, case par case, dans la matrice.`}
                </p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button outline onClick={close} disabled={isSubmitting}>
                Annuler
              </Button>
              <Button
                color="red"
                onClick={submit}
                disabled={isSubmitting || isCreating || loadingInterventions}
              >
                {isSubmitting ? "Interruption..." : "Interrompre"}
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
